// components/footfall/PlanScenarios.tsx
'use client';

import { useEffect, useState } from 'react';
import { useKernelStore } from '@/kernel/kernelStores';
import { campaignLabel } from '#lib/leasing/campaignCard';
import type { CampaignDTO } from '#types/types';
import { useKernel } from '@/kernel/Kernel';
import { BrandMark } from '@/components/ui/BrandMark';
import type { PulseWorkspace } from './pulseDerive';
import { euro, unitName } from '#lib/footfall/pulse';
import { loadCentreCampaigns, selectShop, setPlanShortlist, switchScenario } from './pulseState';

const t = (fontSize: number) => ({ fontSize });

/** A centre's Source runs, newest first: the shortlists a plan can be tied to. */
export function useCentreCampaigns(propertyId: string | null) {
  const { kernel } = useKernel();
  const [state, setState] = useState<{ key: string | null; ids: string[]; status: 'loading' | 'ready' | 'error' }>({ key: null, ids: [], status: 'loading' });
  const registry = useKernelStore(s => s.inventory.Campaign.registry);
  useEffect(() => {
    const k = kernel.current;
    if (!k || !propertyId) return;
    let live = true;
    loadCentreCampaigns(k, propertyId).then(ids => { if (live) setState({ key: propertyId, ids, status: 'ready' }); }, () => { if (live) setState({ key: propertyId, ids: [], status: 'error' }); });
    return () => { live = false; };
  }, [kernel, propertyId]);
  const current = state.key === propertyId ? state : { ids: [], status: 'loading' as const };
  return { status: current.status, campaigns: current.ids.flatMap(id => registry[id] ? [registry[id] as CampaignDTO] : []) };
}
/** How a Source run reads in a picker: its name, how many brands it ranked, when. */
export const campaignOption = (c: CampaignDTO, fr: boolean) =>
  `${campaignLabel(c, fr ? 'Recherche' : 'Run')} · ${c.resultCount ?? c.targetLeadCount} ${fr ? 'enseignes' : 'brands'} · ${new Date(c.createdAt).toLocaleDateString(fr ? 'fr-FR' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;

/** The Source run the scenario on screen is built on: its shortlist is the brands it places. Each scenario has its own (2026-10-14). */
function PlanShortlist({ w, fr }: { w: PulseWorkspace; fr: boolean }) {
  const { kernel } = useKernel();
  const campaignId = useKernelStore(s => s.pulse.draft?.campaignId ?? null);
  const { status, campaigns } = useCentreCampaigns(w.property?.id ?? null);
  return <div className="mt-3 rounded-[16px] bg-[#1B1B1B] p-3" data-testid="plan-shortlist">
    <div className="flex items-baseline justify-between gap-2">
      <span className="font-semibold text-[#ECECEC]" style={t(14)}>{fr ? 'Recherche Source du scénario' : 'Scenario’s Source run'}</span>
      {w.shortlistStatus === 'ready' && <span className="text-[#8E8E8E]" style={t(12)}>{w.shortlist.length} {fr ? 'enseignes classées' : 'ranked brands'}</span>}
    </div>
    {!campaignId && <p className="mt-1 text-[#AFAFAF]" style={t(12)}>{fr ? 'Ce scénario n’est bâti sur aucune recherche Source : choisissez celle dont il place les enseignes.' : 'This scenario is not built on a Source run yet: choose the one whose brands it places.'}</p>}
    <select aria-label={fr ? 'Recherche Source du scénario' : 'Scenario’s Source run'} value={campaignId ?? ''} disabled={status !== 'ready' || !campaigns.length}
      onChange={e => setPlanShortlist(kernel.current, e.target.value)}
      className="mt-2 h-9 w-full rounded-[12px] border border-[#2A2A2A] bg-[#141414] px-2 text-[#ECECEC] focus:border-[#444444] focus:outline-none" style={t(13)}>
      {!campaignId && <option value="" disabled>{status === 'loading' ? (fr ? 'Chargement' : 'Loading') : fr ? 'Choisir une recherche' : 'Choose a Source run'}</option>}
      {campaigns.map(c => <option key={c.id} value={c.id}>{campaignOption(c, fr)}</option>)}
    </select>
    {status === 'ready' && !campaigns.length && <p className="mt-1 text-[#8E8E8E]" style={t(12)}>{fr ? 'Aucune recherche Source sur ce centre : lancez-en une dans Source.' : 'No Source run on this centre yet: run one in Source.'}</p>}
  </div>;
}
const pct = (o: { let: number; total: number }) => `${Math.round(o.let / Math.max(1, o.total) * 100)}%`;

/**
 * The plan's scenarios side by side: each one's reasoning, what it is worth and the moves it makes. Clicking a scenario
 * puts it on the map; the agent writes them, the user compares and picks.
 */
export function PlanScenarios({ w, fr }: { w: PulseWorkspace; fr: boolean }) {
  const { kernel } = useKernel();
  const k = kernel.current;
  const priced = w.shortlistStatus === 'ready' && !!w.scenario.yieldPct;
  const occupancyToday = w.scenarios[0]?.totals.occupancyToday;
  return <div className="px-4 pb-6 pt-1" data-testid="plan-scenarios">
    <p className="text-[#8E8E8E]" style={t(13)}>{fr ? 'Demandez à l’agent de proposer des scénarios : il calcule les données du centre, vérifie la short-list et justifie chaque choix.' : 'Ask the agent to propose scenarios: it works through the centre’s data, checks the shortlist and explains each choice.'}</p>
    <PlanShortlist w={w} fr={fr} />
    {occupancyToday && <p className="mt-3 text-[#AFAFAF]" style={t(12)}>{fr ? 'Aujourd’hui' : 'Today'} · {fr ? 'occupation' : 'occupancy'} {pct(occupancyToday)}</p>}
    <div className="mt-2 space-y-3">
      {w.scenarios.map(s => <ScenarioCard key={s.id} s={s} priced={priced} fr={fr} onOpen={() => switchScenario(k, s.id)}
        onShop={shopId => { if (!s.active) switchScenario(k, s.id); selectShop(k, shopId); }} logoOf={id => w.shortlist.find(b => b.id === id)?.logoUrl}
        unitOf={shopId => { const f = w.places.find(p => p.feature.id === shopId)?.feature; return f ? unitName(f) : shopId; }} />)}
    </div>
  </div>;
}

type Scenario = PulseWorkspace['scenarios'][number];
/** One scenario: its header switches the map to it; its reasoning reads in a few lines, all of it on demand. */
function ScenarioCard({ s, priced, fr, onOpen, onShop, logoOf, unitOf }: { s: Scenario; priced: boolean; fr: boolean; onOpen: () => void; onShop: (shopId: string) => void; logoOf: (brandId: string) => string | null | undefined; unitOf: (shopId: string) => string }) {
  const [more, setMore] = useState(false);
  return <div className={`rounded-[16px] bg-[#1B1B1B] p-3 ${s.active ? 'ring-1 ring-white/[0.16]' : ''}`}>
    <button type="button" onClick={onOpen} aria-pressed={s.active} className="block w-full rounded-[10px] text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#8AAAEE]">
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap font-semibold text-[#ECECEC]" style={t(14)}>{s.name}</span>
        {s.active ? <span className="shrink-0 rounded-full bg-white/[0.1] px-2 py-0.5 text-[#ECECEC]" style={t(11)}>{fr ? 'Sur le plan' : 'On the map'}</span>
          : <span className="shrink-0 text-[#8E8E8E]" style={t(12)}>{fr ? 'Afficher' : 'Show'}</span>}
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2">
        <Metric label={fr ? 'Valeur créée' : 'Value created'} value={priced ? euro(s.totals.valueCreated, fr) : '–'} />
        <Metric label={fr ? 'Gain / an' : 'Uplift / yr'} value={priced ? euro(s.totals.uplift, fr) : '–'} />
        <Metric label={fr ? 'Occupation' : 'Occupancy'} value={pct(s.totals.occupancyScenario)} />
      </div>
    </button>
    {s.thesis && <div className="mt-3">
      <p className={`whitespace-pre-line text-[#D6D6D6] ${more ? '' : 'line-clamp-4'}`} style={t(13)}>{s.thesis}</p>
      <button type="button" className="mt-1 text-[#8AAAEE] hover:underline" style={t(12)} onClick={() => setMore(v => !v)}>{more ? (fr ? 'Réduire' : 'Show less') : (fr ? 'Lire le raisonnement' : 'Read the reasoning')}</button>
    </div>}
    {s.layout.length > 0 && <div className="mt-3 space-y-1" data-testid="scenario-layout">
      {s.layout.map((c, i) => <p key={i} className="text-[#D6D6D6]" style={t(13)}>
        {c.kind === 'split' ? `${fr ? 'Diviser' : 'Split'} ${unitOf(c.shopId)} ${fr ? 'en' : 'into'} ${c.shares.length}` : `${fr ? 'Réunir' : 'Merge'} ${c.shopIds.map(unitOf).join(' + ')}`}
        <span className="text-[#8E8E8E]" style={{ fontSize: 'inherit' }}> · {fr ? 'travaux' : 'works'} {euro(c.works, fr)}</span>
      </p>)}
    </div>}
    <div className="mt-3">
      {s.moves.length === 0 ? <p className="text-[#8E8E8E]" style={t(12)}>{fr ? 'Aucun mouvement pour l’instant.' : 'No move yet.'}</p>
        : s.moves.map(m => <button key={m.shopId} type="button" onClick={() => onShop(m.shopId)} className="-mx-1 flex w-[calc(100%+8px)] items-center gap-2 rounded-[8px] px-1 py-1 text-left hover:bg-white/[0.06] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#8AAAEE]">
          <span className="w-14 shrink-0 tabular-nums text-[#8E8E8E]" style={t(12)}>{unitOf(m.shopId)}</span>
          {m.type === 'relet' && m.brandName && <BrandMark logoUrl={m.brandId ? logoOf(m.brandId) : null} name={m.brandName} size={18} radius={4} />}
          <span className="min-w-0 flex-1">
            <span className="block overflow-hidden text-ellipsis whitespace-nowrap text-[#ECECEC]" style={t(13)}>{m.type === 'vacate' ? (fr ? 'Libérer' : 'Vacate') : (m.brandName ?? (fr ? 'Relouer, sans enseigne choisie' : 'Re-let, no brand chosen'))}</span>
            {s.unpriced[m.shopId] && <span className="block text-[#8E8E8E]" style={t(12)}>{fr ? 'Non chiffré' : 'Not priced'} · {s.unpriced[m.shopId]}</span>}
          </span>
        </button>)}
    </div>
  </div>;
}

function Metric({ label, value }: { label: string; value: string }) {
  return <div className="min-w-0"><div className="text-[#8E8E8E]" style={t(11)}>{label}</div><div className="overflow-hidden text-ellipsis whitespace-nowrap font-semibold tabular-nums text-[#ECECEC]" style={t(14)}>{value}</div></div>;
}
